/**
 * Pure TEA update for outline chrome (settings dialog + header new menu).
 *
 * Shell: `EditorOutlinePane.vue`.
 */
import { assertNever } from "../lib/assertNever";
import { nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";
import type { ProjectSettingsSectionId } from "./projectSettingsSectionId";

export type OutlineChromeModel = {
  readonly settingsOpen: boolean;
  readonly settingsSection: Maybe<ProjectSettingsSectionId>;
  readonly newMenuOpen: boolean;
};

export const initialOutlineChromeModel: OutlineChromeModel = {
  settingsOpen: false,
  settingsSection: nothing,
  newMenuOpen: false,
};

export type OutlineChromeMsg =
  | {
      readonly type: "OpenSettings";
      readonly section: Maybe<ProjectSettingsSectionId>;
    }
  | { readonly type: "CloseSettings" }
  | { readonly type: "ToggleNewMenu" }
  | { readonly type: "CloseNewMenu" };

export type OutlineChromeCmd = {
  readonly kind: "cancelCreating";
};

export function updateOutlineChrome(
  model: OutlineChromeModel,
  msg: OutlineChromeMsg,
): readonly [OutlineChromeModel, readonly OutlineChromeCmd[]] {
  switch (msg.type) {
    case "OpenSettings":
      return [
        {
          ...model,
          settingsOpen: true,
          settingsSection: msg.section,
          newMenuOpen: false,
        },
        [{ kind: "cancelCreating" }],
      ];
    case "CloseSettings":
      return [{ ...model, settingsOpen: false, settingsSection: nothing }, []];
    case "ToggleNewMenu":
      return [{ ...model, newMenuOpen: model.newMenuOpen === false }, []];
    case "CloseNewMenu":
      return [{ ...model, newMenuOpen: false }, []];
    default:
      return assertNever(msg);
  }
}

export function openSettingsSection(
  section: Maybe<ProjectSettingsSectionId> = nothing,
): OutlineChromeMsg {
  return {
    type: "OpenSettings",
    section,
  };
}

export const updateOutlineChromeFn: UpdateFn<
  OutlineChromeModel,
  OutlineChromeMsg,
  OutlineChromeCmd
> = updateOutlineChrome;
